/**
 * Custom error class for API fetch errors.
 * Wraps the Response object and provides convenience methods for error handling.
 */
class FetchError<T = Response> extends Error {
  public error: T;

  constructor(error: T) {
    super(FetchError.extractMessage(error));
    this.name = 'FetchError';
    this.error = error;
  }

  /**
   * HTTP status code of the response.
   * Returns undefined if error is not a Response object.
   */
  get status(): number | undefined {
    if (this.error instanceof Response) {
      return this.error.status;
    }
    return (this.error as { status?: number })?.status;
  }

  /**
   * Whether the error is retryable (5xx errors or rate limiting).
   */
  get isRetryable(): boolean {
    const status = this.status;
    if (!status) return false;
    return status >= 500 || status === 429;
  }

  /**
   * Whether the error is a 404 Not Found.
   */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Whether the error is a 409 Conflict (e.g., duplicate).
   */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /**
   * Whether the error is a 401 Unauthorized.
   */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Whether the error is a 403 Forbidden.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Whether the error is a client error (4xx).
   */
  get isClientError(): boolean {
    const status = this.status;
    if (!status) return false;
    return status >= 400 && status < 500;
  }

  /**
   * Whether the error is a server error (5xx).
   */
  get isServerError(): boolean {
    const status = this.status;
    if (!status) return false;
    return status >= 500;
  }

  /**
   * Extract a message from the error object.
   */
  private static extractMessage<T>(error: T): string {
    if (error instanceof Response) {
      return `HTTP ${error.status}: ${error.statusText}`;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message);
    }
    return 'An error occurred';
  }
}

export default FetchError;
